import React, { useState, useEffect } from 'react';
import { ArrowLeft, Users, Plus, Trash2, Shield, Search, X } from 'lucide-react';
import { useCompanies } from '../../hooks/useCompanies';
import { usersAPI } from '../../api/users';

const ROLE_LABEL = {
  super_admin:   'Super Admin',
  company_admin: 'Administrador',
  supervisor:    'Supervisor',
  rrhh:          'RRHH',
  employee:      'Empleado',
};

const ROLE_COLOR = {
  super_admin:   'bg-red-50 text-red-600',
  company_admin: 'bg-purple-50 text-purple-600',
  supervisor:    'bg-orange-50 text-orange-600',
  rrhh:          'bg-blue-50 text-blue-600',
  employee:      'bg-green-50 text-green-600',
};

const CompanyUsers = ({ company, onClose }) => {
  const { getCompanyUsers, assignUser, removeUser, loading } = useCompanies();

  const [companyUsers, setCompanyUsers] = useState([]);
  const [allUsers, setAllUsers]         = useState([]);
  const [showAddForm, setShowAddForm]   = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState('employee');
  const [searchTerm, setSearchTerm]     = useState('');

  useEffect(() => {
    loadCompanyUsers();
    loadAllUsers();
  }, [company.id]); // eslint-disable-line

  const loadCompanyUsers = async () => {
    const result = await getCompanyUsers(company.id);
    if (result.success) setCompanyUsers(result.data);
  };

  const loadAllUsers = async () => {
    try {
      const data = await usersAPI.getAll();
      setAllUsers(Array.isArray(data) ? data : []);
    } catch {
      setAllUsers([]);
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    if (!selectedUserId) return;
    const result = await assignUser(company.id, selectedUserId, { role: selectedRole, permisos: {} });
    if (result.success) {
      setShowAddForm(false);
      setSelectedUserId('');
      setSelectedRole('employee');
      loadCompanyUsers();
    } else {
      alert(result.error);
    }
  };

  const handleRemove = async (userId, username) => {
    if (!window.confirm(`¿Remover a ${username} de esta compañía?`)) return;
    const result = await removeUser(company.id, userId);
    if (result.success) loadCompanyUsers();
    else alert(result.error);
  };

  const availableUsers = allUsers.filter(u => !companyUsers.some(cu => cu.id === u.id));

  const filtered = companyUsers.filter(u => {
    const q = searchTerm.toLowerCase();
    return !q ||
      u.username?.toLowerCase().includes(q) ||
      u.nombre_completo?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4">

      {/* ── Back button ─────────────────────────────────────────── */}
      <button
        onClick={onClose}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors"
      >
        <ArrowLeft size={16} />
        Volver a Compañías
      </button>

      {/* ── Title card ──────────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl px-6 py-4">
        <h2 className="text-lg font-bold text-gray-900">Usuarios — {company.nombre}</h2>
        <p className="text-sm text-gray-400 mt-0.5">Gestiona los usuarios con acceso a esta compañía</p>
      </div>

      {/* ── Toolbar ─────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar usuario..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500">
              <X size={14} />
            </button>
          )}
        </div>

        <span className="text-sm text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full font-medium ml-auto">
          {filtered.length} usuario{filtered.length !== 1 ? 's' : ''}
        </span>

        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
            showAddForm
              ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          <Plus size={15} />
          Asignar usuario
        </button>
      </div>

      {/* ── Assign form (colapsable) ─────────────────────────────── */}
      {showAddForm && (
        <div className="bg-white border border-blue-200 rounded-xl p-5">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Asignar usuario</p>
          <form onSubmit={handleAssign}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Usuario</label>
                <select
                  value={selectedUserId}
                  onChange={e => setSelectedUserId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white"
                  required
                >
                  <option value="">Seleccionar usuario...</option>
                  {availableUsers.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.username}{u.nombre_completo ? ` — ${u.nombre_completo}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Rol</label>
                <select
                  value={selectedRole}
                  onChange={e => setSelectedRole(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white"
                >
                  <option value="employee">Empleado</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="rrhh">RRHH</option>
                  <option value="company_admin">Administrador</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setShowAddForm(false); setSelectedUserId(''); setSelectedRole('employee'); }}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading || !selectedUserId}
                className="px-4 py-2 text-sm font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Asignando...' : 'Asignar'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Loading ──────────────────────────────────────────────── */}
      {loading && !showAddForm && (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      )}

      {/* ── Empty ────────────────────────────────────────────────── */}
      {!loading && filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <Users className="w-10 h-10 text-gray-200 mb-3" />
          <p className="text-sm text-gray-400">
            {searchTerm ? 'Sin resultados para esa búsqueda' : 'No hay usuarios asignados a esta compañía'}
          </p>
        </div>
      )}

      {/* ── Table ────────────────────────────────────────────────── */}
      {!loading && filtered.length > 0 && (
        <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Usuario</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider hidden md:table-cell">Email</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Rol</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider hidden sm:table-cell">Estado</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map(user => (
                <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                        <span className="text-xs font-semibold text-gray-500">
                          {user.username?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">{user.username}</p>
                        {user.nombre_completo && (
                          <p className="text-xs text-gray-400 truncate">{user.nombre_completo}</p>
                        )}
                      </div>
                    </div>
                  </td>

                  <td className="px-4 py-3 text-gray-500 hidden md:table-cell truncate max-w-[200px]">
                    {user.email || <span className="text-gray-300">—</span>}
                  </td>

                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_COLOR[user.role] || 'bg-gray-50 text-gray-600'}`}>
                      <Shield size={10} />
                      {ROLE_LABEL[user.role] || user.role}
                    </span>
                  </td>

                  <td className="px-4 py-3 hidden sm:table-cell">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      user.is_active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'
                    }`}>
                      {user.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex justify-end">
                      <button
                        onClick={() => handleRemove(user.id, user.username)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Remover usuario"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CompanyUsers;
